/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Alert,
    Pagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PaginationItem from '@mui/material/PaginationItem';
import Sidebar from '../Sidebar/Sidebar';
import Navbar from '../Navbar/Navbar';
import { ordersAPI, settingsAPI } from '../../services/apiService';
import AddOrderDialog from '../AddOrderDialog/AddOrderDialog';
import EditOrderDialog from '../EditOrderDialog/EditOrderDialog';
import CuatomerReceipt from '../CuatomerReceipt/CuatomerReceipt';
import KitchenReceipt from '../KitchenReceipt/KitchenReceipt';
import './orders.css';

function Orders() {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const statusFilter = searchParams.get('status');

    const [orders, setOrders] = useState([]);
    const [taxPercent, setTaxPercent] = useState(0);
    const [addres, setAddres] = useState('');
    const [brandName, setBrandName] = useState('');
    const [logo, setLogo] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);

    const [openItemsDialog, setOpenItemsDialog] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const formatNumberByLanguage = (num) => {
        const locale = i18n.language === 'fa' ? 'fa-IR' : 'en-US';
        return new Intl.NumberFormat(locale).format(num);
    };

    const handleEditClick = (order) => {
        setSelectedOrderId(order.id);
        setEditOpen(true);
    };

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const customerReceiptRef = useRef(null);
    const kitchenReceiptRef = useRef(null);

    const fetchOrders = useCallback(async () => {
        if (!localStorage.getItem('token') || !localStorage.getItem('userGroup')) {
            setError(t('auth_or_group_missing'));
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const [ordersResult, settingsData] = await Promise.all([
                ordersAPI.getAll({ today: true }),
                settingsAPI.getSettings(),
            ]);

            setOrders(Array.isArray(ordersResult) ? ordersResult : []);
            setTaxPercent(Number(settingsData?.tax || 0));
            setBrandName(settingsData?.brand_name || '');
            setLogo(settingsData?.logo || '');
            setPhoneNumber(settingsData?.phone_number || '');
            setAddres(settingsData?.address || '');
        } catch (err) {
            console.error(err);
            setError(err.message || t('failed_to_load_orders'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const handleChangePage = (event, value) => {
        setCurrentPage(value);
    };

    const confirmDelete = (id) => {
        setSelectedOrderId(id);
        setOpenDeleteDialog(true);
    };

    const handleCloseDelete = () => {
        setOpenDeleteDialog(false);
        setSelectedOrderId(null);
    };
    const getTableDisplayName = (order) => {
        const tableId = order?.table_id ?? order?.table;
        if (tableId === null || tableId === undefined || tableId === '') {
            return t('take_away_order');
        }

        return tableId;
    };

    const handleConfirmDelete = async () => {
        if (!selectedOrderId) return;

        try {
            await ordersAPI.remove(selectedOrderId);

            const updatedOrders = orders.filter((order) => order.id !== selectedOrderId);
            setOrders(updatedOrders);

            setSnackbar({
                open: true,
                message: t('delete_order_success') || 'Order deleted successfully',
                severity: 'success',
            });
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: t('delete_order_error') || 'Failed to delete order',
                severity: 'error',
            });
        } finally {
            handleCloseDelete();
        }
    };

    const handleAddOrderError = (message) => {
        setSnackbar({
            open: true,
            message: message || t('failed_to_create_order'),
            severity: 'error',
        });
    };

    const handleOpenItemsDialog = (order) => {
        setSelectedOrder(order);
        setOpenItemsDialog(true);
    };

    const handleCloseItemsDialog = () => {
        setOpenItemsDialog(false);
        setSelectedOrder(null);
    };

    const handleOpenAddDialog = () => {
        setOpenAddDialog(true);
    };

    const handleCloseAddDialog = () => {
        setOpenAddDialog(false);
    };

    const handleAddOrderSuccess = (message) => {
        setSnackbar({
            open: true,
            message: message || t('order_created_success') || 'Order created successfully!',
            severity: 'success',
        });
        fetchOrders();
        setCurrentPage(1);
    };
    const handleEditOrderSuccess = (message) => {
        setSnackbar({
            open: true,
            message: t('order_edit_success'),
            severity: 'success',
        });
        fetchOrders();
        setCurrentPage(1);
    };

    const handleEditOrderError = (message) => {
        setSnackbar({
            open: true,
            message: t('failed_to_edit_order'),
            severity: 'error',
        });
    };

    const handleCloseSnackbar = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const getOrderTotal = (order) => {
        if (!order?.items || !Array.isArray(order.items)) return 0;

        return order.items.reduce((sum, item) => sum + Number(item.total_price ?? 0), 0);
    };

    const getOrderDiscount = (order) => Number(order?.discount ?? 0);

    const getTaxableAmount = (order) => {
        const subtotal = getOrderTotal(order);
        const discount = getOrderDiscount(order);

        return Math.max(subtotal - discount, 0);
    };

    const getTaxAmount = (order) => {
        const taxableAmount = getTaxableAmount(order);

        return (taxableAmount * taxPercent) / 100;
    };

    const getOrderTotalPrice = (order) => {
        const taxableAmount = getTaxableAmount(order);
        const taxAmount = getTaxAmount(order);

        return taxableAmount + taxAmount;
    };

    const getStatusClassName = (status) => {
        const normalizedStatus = String(status || '').toLowerCase();

        switch (normalizedStatus) {
            case 'preparing':
                return 'status_badge status_preparing';
            case 'pending':
                return 'status_badge status_pending';
            case 'served':
                return 'status_badge status_served';
            case 'paid':
                return 'status_badge status_paid';
            case 'cancelled':
                return 'status_badge status_cancelled';
            default:
                return 'status_badge status_unknown';
        }
    };

    const formatReceiptTime = (dateString) => {
        if (!dateString) return '-';

        return new Date(dateString).toLocaleTimeString(i18n.language === 'fa' ? 'fa-IR' : 'en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    };

    const getTranslatedStatus = (status) => {
        const normalizedStatus = String(status || '').toLowerCase();

        if (!normalizedStatus) return '-';

        return t(`order_status.${normalizedStatus}`) || status;
    };
    const getDailyOrderNumber = (currentOrder) => {
        if (!currentOrder || !orders.length) return '-';

        const orderDate = new Date(currentOrder.created_at).toDateString();

        const sameDayOrders = orders
            .filter((o) => new Date(o.created_at).toDateString() === orderDate)
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        const index = sameDayOrders.findIndex((o) => o.id === currentOrder.id);

        return index !== -1 ? index + 1 : '-';
    };

    const filteredOrders = statusFilter
        ? orders.filter(
              (order) => String(order.status || '').toLowerCase() === statusFilter.toLowerCase()
          )
        : orders;

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    const currentItems = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
    const handlePrintBothReceipts = async () => {
        if (!customerReceiptRef.current || !kitchenReceiptRef.current) {
            return;
        }

        customerReceiptRef.current.print();

        setTimeout(() => {
            kitchenReceiptRef.current.print();
        }, 500);
    };

    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
        if (filteredOrders.length === 0) {
            setCurrentPage(1);
        }
    }, [filteredOrders.length, totalPages, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter]);

    return (
        <div className="orders_page">
            <Sidebar />
            <div className="orders_main">
                <Navbar />

                <div className="orders_content">
                    <div className="orders_header">
                        <h2>{t('orders') || 'Orders'}</h2>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleOpenAddDialog}
                            className="add_order_button"
                        >
                            {t('add_order') || 'Add Order'}
                        </Button>
                    </div>

                    {loading && <p>{t('loading')}</p>}

                    {error && (
                        <p>
                            {t('error')}: {error}
                        </p>
                    )}

                    {!loading && !error && (
                        <TableContainer component={Paper} className="orders_table">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center">
                                            {t('customer_name') || 'Order ID'}
                                        </TableCell>
                                        <TableCell align="center">
                                            {t('table_number') || 'Table ID'}
                                        </TableCell>
                                        <TableCell align="center">
                                            {t('status') || 'Status'}
                                        </TableCell>
                                        <TableCell align="center">
                                            {t('created_date') || 'Updated Date'}
                                        </TableCell>
                                        <TableCell align="center">
                                            {t('discount') || 'Total Price'}
                                        </TableCell>
                                        <TableCell align="center">
                                            {t('final_total') || 'Total Price'}
                                        </TableCell>
                                        <TableCell align="center">
                                            {t('receipts') || 'Items'}
                                        </TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {currentItems.map((order) => (
                                        <TableRow key={order.id}>
                                            <TableCell
                                                data-label={t('customer_name')}
                                                align="center"
                                            >
                                                {order.customer == null
                                                    ? t('walk_in_customer')
                                                    : order.customer_name || t('walk_in_customer')}
                                            </TableCell>

                                            <TableCell
                                                data-label={t('table_number') || 'Table ID'}
                                                align="center"
                                            >
                                                {getTableDisplayName(order)}
                                            </TableCell>

                                            <TableCell
                                                data-label={t('status') || 'Status'}
                                                align="center"
                                            >
                                                <span className={getStatusClassName(order.status)}>
                                                    {getTranslatedStatus(order.status)}
                                                </span>
                                            </TableCell>

                                            <TableCell
                                                data-label={t('created_date') || 'Updated Date'}
                                                align="center"
                                                className="orderCreatedDateAt"
                                            >
                                                {order.created_at
                                                    ? new Date(order.created_at).toLocaleString(
                                                          i18n.language === 'fa'
                                                              ? 'fa-IR'
                                                              : 'en-US',
                                                          {
                                                              day: '2-digit',
                                                              hour: '2-digit',
                                                              minute: '2-digit',
                                                              year: 'numeric',
                                                              month: '2-digit',
                                                              hour12: false,
                                                          }
                                                      )
                                                    : '-'}
                                            </TableCell>

                                            <TableCell
                                                data-label={t('total_price') || 'Total Price'}
                                                align="center"
                                            >
                                                {order.discount.toLocaleString(
                                                    i18n.language === 'fa' ? 'fa-IR' : 'en-US'
                                                )}
                                            </TableCell>

                                            <TableCell
                                                data-label={t('total_price') || 'Total Price'}
                                                align="center"
                                            >
                                                {getOrderTotalPrice(order).toLocaleString(
                                                    i18n.language === 'fa' ? 'fa-IR' : 'en-US'
                                                )}
                                            </TableCell>

                                            <TableCell
                                                data-label={t('items') || 'Items'}
                                                align="center"
                                            >
                                                <Button
                                                    className="viewOrderButton"
                                                    onClick={() => handleOpenItemsDialog(order)}
                                                >
                                                    {t('view_factors') || 'View Items'}
                                                </Button>
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                                className="action_buttons"
                                                data-label={t('actions') || 'Actions'}
                                            >
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => confirmDelete(order.id)}
                                                >
                                                    {t('delete')}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="success"
                                                    onClick={() => handleEditClick(order)}
                                                >
                                                    {t('edit')}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {filteredOrders.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                {t('no_orders_found') || 'No orders found'}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>

                            {totalPages > 1 && (
                                <div className="orders_pagination">
                                    <Pagination
                                        count={totalPages}
                                        page={currentPage}
                                        onChange={handleChangePage}
                                        color="primary"
                                        variant="outlined"
                                        hidePrevButton
                                        hideNextButton
                                        renderItem={(item) => (
                                            <PaginationItem
                                                // eslint-disable-next-line react/jsx-props-no-spreading
                                                {...item}
                                                page={
                                                    item.page
                                                        ? formatNumberByLanguage(item.page)
                                                        : item.page
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            )}
                        </TableContainer>
                    )}

                    <Dialog open={openDeleteDialog} onClose={handleCloseDelete}>
                        <DialogTitle>{t('confirm_delete')}</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                {t('confirm_delete_order') ||
                                    'Are you sure you want to delete this order?'}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDelete} color="primary">
                                {t('no')}
                            </Button>
                            <Button onClick={handleConfirmDelete} color="error" autoFocus>
                                {t('yes_delete')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        className="ordersPageReiciptsDialog"
                        open={openItemsDialog}
                        onClose={handleCloseItemsDialog}
                        maxWidth="sm"
                    >
                        <DialogContent>
                            <CuatomerReceipt
                                ref={customerReceiptRef}
                                order={selectedOrder}
                                orders={orders}
                                brandName={brandName}
                                taxPercent={taxPercent}
                                logo={logo}
                                phoneNumber={phoneNumber}
                                addres={addres}
                            />

                            <div style={{ display: 'none' }}>
                                <KitchenReceipt
                                    ref={kitchenReceiptRef}
                                    order={selectedOrder}
                                    orders={orders}
                                />
                            </div>
                        </DialogContent>
                        <DialogActions className="orderPageReciptsDialogFooterButtons">
                            <Button
                                onClick={() => customerReceiptRef.current?.print()}
                                color="primary"
                            >
                                {t('print_customer_receipt') || 'Customer Receipt'}
                            </Button>

                            <Button
                                onClick={() => kitchenReceiptRef.current?.print()}
                                color="primary"
                            >
                                {t('print_kitchen_receipt') || 'Kitchen Receipt'}
                            </Button>

                            <Button onClick={handleCloseItemsDialog}>{t('close')}</Button>
                        </DialogActions>
                    </Dialog>

                    <AddOrderDialog
                        open={openAddDialog}
                        onClose={handleCloseAddDialog}
                        onSuccess={handleAddOrderSuccess}
                        onError={handleAddOrderError}
                    />
                    <EditOrderDialog
                        open={editOpen}
                        orderId={selectedOrderId}
                        taxPercent={taxPercent}
                        onClose={() => {
                            setEditOpen(false);
                            setSelectedOrderId(null);
                        }}
                        onSuccess={handleEditOrderSuccess}
                        onError={handleEditOrderError}
                    />

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={4000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </div>
            </div>
        </div>
    );
}

export default Orders;
